/**
 * Canonical store identifiers and listing URLs.
 *
 * The Android package and the App Store id live here so marketing links, the
 * download page, and review prompts cannot drift. Provider tokens are not
 * stored here: callers pass them in, and a missing or invalid token is left
 * off the URL.
 *
 * No Node builtins. The web client bundles this file.
 */

const ANDROID_PACKAGE_NAME = 'app.budgit.android';
const IOS_APP_STORE_ID = '6745227970';
const STORE_DOWNLOAD_BASE = 'https://budgit.app/download';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

const PLAY_DETAILS_URL = 'https://play.google.com/store/apps/details';
const APP_STORE_LISTING_URL = `https://apps.apple.com/app/id${IOS_APP_STORE_ID}`;

function sanitizeProviderToken(raw) {
  if (raw == null) return '';
  const token = String(raw).trim();
  if (!/^[A-Za-z0-9]{1,32}$/.test(token)) return '';
  return token;
}

function sanitizeCampaignToken(raw) {
  if (raw == null) return '';
  return String(raw).trim().replace(/[^A-Za-z0-9._~-]/g, '').slice(0, 40);
}

function buildPlayStoreDetailsUrl({ referrer, showAllReviews } = {}) {
  const url = new URL(PLAY_DETAILS_URL);
  url.searchParams.set('id', ANDROID_PACKAGE_NAME);
  if (referrer) url.searchParams.set('referrer', referrer);
  if (showAllReviews) url.searchParams.set('showAllReviews', 'true');
  return url.toString();
}

function buildPlayMarketUrl({ showAllReviews } = {}) {
  const params = new URLSearchParams();
  params.set('id', ANDROID_PACKAGE_NAME);
  if (showAllReviews) params.set('showAllReviews', 'true');
  return `market://details?${params.toString()}`;
}

function buildAppStoreListingUrl({ providerToken, campaignToken, writeReview } = {}) {
  const url = new URL(APP_STORE_LISTING_URL);
  const pt = sanitizeProviderToken(providerToken);
  const ct = sanitizeCampaignToken(campaignToken);
  if (pt) url.searchParams.set('pt', pt);
  if (ct) url.searchParams.set('ct', ct);
  if (writeReview) url.searchParams.set('action', 'write-review');
  return url.toString();
}

function utmParamsFromSearch(search) {
  const raw = search == null ? '' : String(search);
  const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
  const utm = {};
  UTM_KEYS.forEach((key) => {
    const value = (params.get(key) || '').trim();
    if (value) utm[key] = value;
  });
  return utm;
}

function referrerFromUtm(utm = {}) {
  const params = new URLSearchParams();
  UTM_KEYS.forEach((key) => {
    if (utm[key]) params.set(key, utm[key]);
  });
  const qs = params.toString();
  return qs || '';
}

module.exports = {
  ANDROID_PACKAGE_NAME,
  IOS_APP_STORE_ID,
  STORE_DOWNLOAD_BASE,
  UTM_KEYS,
  sanitizeProviderToken,
  sanitizeCampaignToken,
  buildPlayStoreDetailsUrl,
  buildPlayMarketUrl,
  buildAppStoreListingUrl,
  utmParamsFromSearch,
  referrerFromUtm,
};
